import { FC, useState, useCallback, useEffect, useMemo } from 'react';
import { Box, Grid, CircularProgress, Alert, Typography, Select, MenuItem, FormControl, Button } from '@mui/material';

import { Sidebar } from '../../../common/components/Sidebar';
import SectionBar from '../../../common/components/SectionBar/SectionBar';
import { StatusTable } from '../../../common/components/StatusTable/StatusTable';
import { NotificationSnackbar } from '../../../common/components/Snackbar';
import { useNavItems, usePermission } from '../../../hooks';
import { useUser } from '../../../hooks/useUser';
import { useE55Status } from './useE55Status';
import { useModuleOperators } from '../../../hooks/useModuleOperators';
import { StyledSegmentedControl, StyledSegmentButton } from '../../../common/components/SectionBar/SectionBar.styled';

import { StyledContainer, MainContent, TableSection } from './e55Status.styled';

const E55Status: FC = () => {
  const [sidebarOpen, setSidebarOpen] = useState<boolean>(true);
  const navItems = useNavItems();
  const { hasPermission, isLoading: permissionLoading } = usePermission('/e55/status', '');
  const { data: userData, isLoading: userLoading } = useUser();

  // State for filters from URL parameters
  const [yearFilter, setYearFilter] = useState<string | undefined>(undefined);
  const [ppoFilter, setPpoFilter] = useState<string | undefined>(undefined);
  const [inboundFilter, setInboundFilter] = useState<boolean | undefined>(undefined);
  const [openFilter, setOpenFilter] = useState<boolean | undefined>(undefined);

  // Local filter state for form inputs (before submit)
  const [selectedYear, setSelectedYear] = useState<string>('');
  const [selectedPpo, setSelectedPpo] = useState<string>('');
  const [selectedDirection, setSelectedDirection] = useState<string>('all');
  const [selectedStatus, setSelectedStatus] = useState<string>('all');
  const [hasSubmitted, setHasSubmitted] = useState<boolean>(false);

  // Section bar form state (separate from API filters)
  const [sectionBarYear, setSectionBarYear] = useState<string>('');
  const [sectionBarPpo, setSectionBarPpo] = useState<string>('');
  const [sectionBarDirection, setSectionBarDirection] = useState<string>('all');
  const [sectionBarStatus, setSectionBarStatus] = useState<string>('all');

  // Get effective operators + cascading logic for e55 module
  const {
    operators,
    availableYears,
    operatorsForYear,
    relationsData,
    isPpoLoading: isPposLoading,
    ppoError,
  } = useModuleOperators('e55', { selectedYear, selectedPpo });

  // Generate years — always show all available years (not filtered by DO)
  const years = useMemo(() => {
    return availableYears.map(year => year.toString());
  }, [availableYears]);

  // Generate PPO options with cascading logic
  const ppoOptions = useMemo(() => {
    const options: { id: string; name: string; disabled: boolean }[] = [];
    const targetDOsToUse = selectedYear ? operatorsForYear : operators;
    targetDOsToUse.forEach((targetDO) => {
      options.push({
        id: targetDO,
        name: targetDO,
        disabled: false,
      });
    });
    return options;
  }, [operators, operatorsForYear, selectedYear]);

  // Initialize section bar state when hasSubmitted becomes true
  useEffect(() => {
    if (hasSubmitted) {
      setSectionBarYear(selectedYear);
      setSectionBarPpo(selectedPpo);
      setSectionBarDirection(selectedDirection);
      setSectionBarStatus(selectedStatus);
    }
  }, [hasSubmitted, selectedYear, selectedPpo, selectedDirection, selectedStatus]);

  // Filter handling functions
  const handleSubmit = () => {
    // Convert form values to API parameters
    const year = selectedYear || undefined;
    const ppo = selectedPpo || undefined;
    
    let inbound: boolean | undefined = undefined;
    if (selectedDirection === 'inbound') inbound = true;
    else if (selectedDirection === 'outbound') inbound = false;
    
    let open: boolean | undefined = undefined;
    if (selectedStatus === 'open') open = true;
    else if (selectedStatus === 'closed') open = false;

    // Update filters which will trigger API call
    setYearFilter(year);
    setPpoFilter(ppo);
    setInboundFilter(inbound);
    setOpenFilter(open);
    setHasSubmitted(true);
    
    // Force refetch to ensure request is made even if filters haven't changed
    refetch();
  };

  const handleSectionBarSubmit = () => {
    // Convert section bar form values to API parameters
    const year = sectionBarYear || undefined;
    const ppo = sectionBarPpo || undefined;
    
    let inbound: boolean | undefined = undefined;
    if (sectionBarDirection === 'inbound') inbound = true;
    else if (sectionBarDirection === 'outbound') inbound = false;
    
    let open: boolean | undefined = undefined;
    if (sectionBarStatus === 'open') open = true;
    else if (sectionBarStatus === 'closed') open = false;

    // Update filters which will trigger API call
    setYearFilter(year);
    setPpoFilter(ppo);
    setInboundFilter(inbound);
    setOpenFilter(open);
    
    // Force refetch to ensure request is made even if filters haven't changed
    refetch();
  };

  const handleReset = () => {
    setSelectedYear('');
    setSelectedPpo('');
    setSelectedDirection('all');
    setSelectedStatus('all');
    setYearFilter(undefined);
    setPpoFilter(undefined);
    setInboundFilter(undefined);
    setOpenFilter(undefined);
    setHasSubmitted(false);
  };

  const handleSectionBarReset = () => {
    setSectionBarYear('');
    setSectionBarPpo('');
    setSectionBarDirection('all');
    setSectionBarStatus('all');
    setYearFilter(undefined);
    setPpoFilter(undefined);
    setInboundFilter(undefined);
    setOpenFilter(undefined);
    refetch();
  };

  // Handle DO selection change — years are independent of the selected DO
  const handleDOChange = (value: string) => {
    setSelectedPpo(value);
  };

  // Handle year change with cascading behavior
  const handleYearChange = (value: string) => {
    // Check if the currently selected DO is available for this year
    if (selectedPpo) {
      const relationsForCurrentDO = relationsData?.byTargetDO?.[selectedPpo];
      const isCurrentDOAvailableForNewYear = relationsForCurrentDO?.some(relation => 
        relation.year === parseInt(value) &&
        relation.modules.some(module => 
          module.name === 'e55' && module.enabled
        )
      );
      
      if (!isCurrentDOAvailableForNewYear) {
        setSelectedPpo('');
      }
    }
    setSelectedYear(value);
  };

  // Use the E55Status hook with the filters
  const { 
    data: statusResponseData, 
    isLoading: statusLoading, 
    isError: statusError, 
    error: statusErrorDetails,
    refetch 
  } = useE55Status({
    year: yearFilter,
    ppo: ppoFilter,
    inbound: inboundFilter,
    open: openFilter
  });

  // Debug logging to see what's happening with the filters
  useEffect(() => {
    console.log('E55 Status Debug:', {
      hasSubmitted,
      selectedDirection,
      sectionBarDirection,
      inboundFilter,
      yearFilter,
      ppoFilter,
      openFilter,
      statusResponseData: statusResponseData ? 'Has data' : 'No data'
    });
  }, [hasSubmitted, selectedDirection, sectionBarDirection, inboundFilter, yearFilter, ppoFilter, openFilter, statusResponseData]);

  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'info';
  }>({
    open: false,
    message: '',
    severity: 'success',
  });

  const handleSnackbarClose = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  const handleSidebarToggle = () => {
    setSidebarOpen((prev) => !prev);
  };

  // Show loading indicator
  const showLoading = statusLoading || permissionLoading || userLoading;

  // Create custom filters for SectionBar when results are shown
  const customFiltersForSectionBar = useMemo(() => {
    if (!hasSubmitted) return undefined;

    return {
      e55StatusPpo: sectionBarPpo,
      e55StatusYear: sectionBarYear,
      e55StatusDirection: sectionBarDirection,
      e55StatusStatus: sectionBarStatus,
      e55StatusPpoOptions: ppoOptions,
      e55StatusYearOptions: years,
      e55StatusIsPpoLoading: isPposLoading,
      onE55StatusPpoChange: setSectionBarPpo,
      onE55StatusYearChange: setSectionBarYear,
      onE55StatusDirectionChange: setSectionBarDirection,
      onE55StatusStatusChange: setSectionBarStatus,
      onE55StatusSubmit: handleSectionBarSubmit,
      onE55StatusReset: handleSectionBarReset,
      e55StatusIsSubmitting: statusLoading,
    };
  }, [
    hasSubmitted,
    sectionBarPpo,
    sectionBarYear,
    sectionBarDirection,
    sectionBarStatus,
    ppoOptions,
    years,
    isPposLoading,
    handleSectionBarSubmit,
    handleSectionBarReset,
    statusLoading,
  ]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column' }} data-testid='e55-status-container'>
      <Grid container>
        {/* First Row - SectionBar */}
        <Grid item xs={12}>
          <SectionBar
            title='e55 Status'
            subtitle='e55 Weights'
            onToggleSidebar={handleSidebarToggle}
            isSidebarOpen={sidebarOpen}
            showFilters={{
              customE55Status: !!hasSubmitted,
            }}
            customFilters={customFiltersForSectionBar}
            data-testid="section-bar"
          />
        </Grid>

        {/* Second Row - Sidebar and MainContent */}
        <Grid item xs={12} sx={{ display: 'flex', position: 'relative' }}>
          {/* Sidebar */}
          {sidebarOpen && (
            <Box sx={{ position: 'relative' }}>
              <Sidebar isOpen={sidebarOpen} onToggle={handleSidebarToggle} navItems={navItems} data-testid="e55-status-sidebar" />
            </Box>
          )}

          {/* Main Content */}
          <MainContent sidebarOpen={sidebarOpen} data-testid='e55-status-main-content'>
            {showLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }} data-testid="loading-indicator">
                <CircularProgress />
              </Box>
            ) : statusError ? (
              <Alert severity='error' data-testid="error-alert">
                Error loading data: {statusErrorDetails instanceof Error ? statusErrorDetails.message : 'Unknown error'}
              </Alert>
            ) : !hasPermission ? (
              <Alert severity='warning' data-testid="permission-error-alert">You don't have permission to view this section</Alert>
            ) : (
              <>
                {/* Filter Section - Hide when results are shown */}
                {!hasSubmitted && (
                  <Box sx={{ p: 3, backgroundColor: '#FFFFFF', borderRadius: 1, mb: 3, maxWidth: '100%' }}>
                    <Grid container spacing={2} p="40px">
                      {/* PPO Dropdown - Full width */}
                      <Typography variant="h3" sx={{ 
                        mb: 1, 
                        mt: 1,
                        ml: 2,
                        fontWeight: 700,
                        fontSize: '24px',
                        lineHeight: '100%',
                        letterSpacing: '0%',
                        color: '#063142'
                      }}>
                        e55 Status
                      </Typography>
                      <Grid item xs={12}>
                        <Typography variant="body2" sx={{ mb: 1, fontWeight: 600, color: '#333' }}>
                          DO
                        </Typography>
                        <FormControl fullWidth size="small">
                          <Select
                            value={selectedPpo}
                            onChange={(e) => handleDOChange(e.target.value)}
                            displayEmpty
                            sx={{ 
                              backgroundColor: 'white',
                              maxWidth: '200px',
                              '& .MuiSelect-select': {
                                padding: '12px 14px',
                              }
                            }}
                          >
                            <MenuItem value="">ALL</MenuItem>
                            {ppoOptions.map((option) => (
                              <MenuItem key={option.id} value={option.name}>
                                {option.name}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>

                      {/* Year Dropdown - Full width */}
                      <Grid item xs={12}>
                        <Typography variant="body2" sx={{ mb: 1, fontWeight: 600, color: '#333' }}>
                          Year
                        </Typography>
                        <FormControl fullWidth size="small">
                          <Select
                            value={selectedYear}
                            onChange={(e) => handleYearChange(e.target.value)}
                            displayEmpty
                            sx={{ 
                              backgroundColor: 'white',
                              maxWidth: '200px',
                              '& .MuiSelect-select': {
                                padding: '12px 14px',
                              }
                            }}
                          >
                            <MenuItem value="">ALL</MenuItem>
                            {years.map((year) => (
                              <MenuItem key={year} value={year}>
                                {year}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>

                      {/* Inbound/Outbound Filter */}
                      <Grid item xs="auto">
                        <StyledSegmentedControl sx={{ mb: 2, width: '208px' }}>
                          <StyledSegmentButton
                            active={selectedDirection === 'all'}
                            onClick={() => setSelectedDirection('all')}
                            sx={{ color: selectedDirection === 'all' ? '#ffffff' : '#00B6BA' }}
                          >
                            All
                          </StyledSegmentButton>
                          <StyledSegmentButton
                            active={selectedDirection === 'inbound'}
                            onClick={() => setSelectedDirection('inbound')}
                            sx={{ color: selectedDirection === 'inbound' ? '#ffffff' : '#00B6BA' }}
                          >
                            Inbound
                          </StyledSegmentButton>
                          <StyledSegmentButton
                            active={selectedDirection === 'outbound'}
                            onClick={() => setSelectedDirection('outbound')}
                            sx={{ color: selectedDirection === 'outbound' ? '#ffffff' : '#00B6BA' }}
                          >
                            Outbound
                          </StyledSegmentButton>
                        </StyledSegmentedControl>
                      </Grid>

                      {/* Open/Closed Filter */}
                      <Grid item xs="auto">
                        <StyledSegmentedControl sx={{ mb: 3, width: '168px' }}>
                          <StyledSegmentButton
                            active={selectedStatus === 'all'}
                            onClick={() => setSelectedStatus('all')}
                            sx={{ color: selectedStatus === 'all' ? '#ffffff' : '#00B6BA' }}
                          >
                            All
                          </StyledSegmentButton>
                          <StyledSegmentButton
                            active={selectedStatus === 'open'}
                            onClick={() => setSelectedStatus('open')}
                            sx={{ color: selectedStatus === 'open' ? '#ffffff' : '#00B6BA' }}
                          >
                            Open
                          </StyledSegmentButton>
                          <StyledSegmentButton
                            active={selectedStatus === 'closed'}
                            onClick={() => setSelectedStatus('closed')}
                            sx={{ color: selectedStatus === 'closed' ? '#ffffff' : '#00B6BA' }}
                          >
                            Closed
                          </StyledSegmentButton>
                        </StyledSegmentedControl>
                      </Grid>

                      {/* Action Buttons */}
                      <Grid item xs={12}>
                        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                          <Button
                            variant="outlined"
                            onClick={handleReset}
                            sx={{
                              minWidth: '100px',
                              borderRadius: '1000px',
                              height: '40px',
                              borderColor: '#dee2e6',
                              color: '#6c757d',
                              backgroundColor: '#f8f9fa',
                              fontWeight: 600,
                              textTransform: 'uppercase',
                              '&:hover': {
                                borderColor: '#adb5bd',
                                backgroundColor: '#e9ecef'
                              }
                            }}
                          >
                            RESET
                          </Button>
                          <Button
                            variant="contained"
                            onClick={handleSubmit}
                            sx={{
                              minWidth: '100px',
                              borderRadius: '1000px',
                              height: '40px',
                              backgroundColor: '#6c757d',
                              color: 'white',
                              fontWeight: 600,
                              textTransform: 'uppercase',
                              '&:hover': {
                                backgroundColor: '#5a6268'
                              }
                            }}
                          >
                            SUBMIT
                          </Button>
                        </Box>
                      </Grid>
                    </Grid>
                  </Box>
                )}



                {/* Status Tables - Only show after submit */}
                {hasSubmitted && (
                  <StyledContainer data-testid="tables-container">
                    {/* Show Inbound Table only when direction is 'all' or 'inbound' */}
                    {(inboundFilter === undefined || inboundFilter === true) && (
                      <TableSection data-testid="inbound-status-section" sx={{ mb: inboundFilter === undefined ? 0 : undefined }}>
                        <StatusTable 
                          data={statusResponseData?.inbound || []}
                          title={`Inbound from * to ${userData?.designatedOperator}`}
                          showQuarters={['q1', 'q2', 'q3', 'q4']}
                          backgroundColor="#F0FFF0"
                        />
                      </TableSection>
                    )}
                    
                    {/* Show Outbound Table only when direction is 'all' or 'outbound' */}
                    {(inboundFilter === undefined || inboundFilter === false) && (
                      <TableSection data-testid="outbound-status-section">
                        <StatusTable 
                          data={statusResponseData?.outbound || []}
                          title={`Outbound from ${userData?.designatedOperator} to *`}
                          showQuarters={['q1', 'q2', 'q3', 'q4']}
                          backgroundColor="#FFF0F0"
                        />
                      </TableSection>
                    )}
                  </StyledContainer>
                )}
              </>
            )}

            {/* Notification Snackbar */}
            <NotificationSnackbar
              open={snackbar.open}
              message={snackbar.message}
              severity={snackbar.severity}
              onClose={handleSnackbarClose}
              data-testid="notification-snackbar"
            />
          </MainContent>
        </Grid>
      </Grid>
    </Box>
  );
};

export default E55Status;
